import React, { useEffect, useState } from 'react';
import { Box, Paper, Typography, useTheme, CircularProgress, Alert, Button } from '@mui/material';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { AppDispatch } from '../../state/store';
import { activateAccountAsync } from '../../state/authentication/authenticationSlice';
import { CustomPaletteColor } from '../../theme';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { useLoginPath } from '../../hooks/useLoginPath';

export const Activate = () => {
  const theme = useTheme();
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const loginPath = useLoginPath();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const [message, setMessage] = useState('');

  useEffect(() => {
    const id = searchParams.get('id');
    const token = searchParams.get('token');

    if (!id || !token) {
      setStatus('error');
      setMessage('Invalid activation link. Missing user ID or token.');
      return;
    }

    dispatch(activateAccountAsync({ id, token }))
      .unwrap()
      .then(() => {
        setStatus('success');
        setMessage('Your account has been activated successfully! You can now sign in.');
      })
      .catch((err: any) => {
        setStatus('error');
        setMessage(typeof err === 'string' ? err : 'Activation failed. The link may have expired or already been used.');
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Box sx={{
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      minHeight: '100vh',
      backgroundColor: theme.palette.background.default,
      p: 2,
    }}>
      <Paper elevation={2} sx={{
        maxWidth: 480,
        width: '100%',
        p: 4,
        borderRadius: 3,
        textAlign: 'center',
        border: `1px solid ${(theme.palette.secondary as CustomPaletteColor)[200]}`,
      }}>
        {status === 'loading' && (
          <>
            <CircularProgress color="secondary" size={48} sx={{ mb: 2 }} />
            <Typography variant="h6">Activating your account...</Typography>
            <Typography variant="body2" color="textSecondary" mt={1}>
              Please wait a moment.
            </Typography>
          </>
        )}

        {status === 'success' && (
          <>
            <CheckCircleOutlineIcon sx={{ fontSize: 56, color: theme.palette.success.main, mb: 2 }} />
            <Typography variant="h6" gutterBottom>Account Activated</Typography>
            <Alert severity="success" sx={{ mb: 3, textAlign: 'left' }}>
              {message}
            </Alert>
            <Button
              variant="contained"
              color="secondary"
              onClick={() => navigate(loginPath)}
              fullWidth
            >
              Go to Sign In
            </Button>
          </>
        )}

        {status === 'error' && (
          <>
            <ErrorOutlineIcon sx={{ fontSize: 56, color: theme.palette.error.main, mb: 2 }} />
            <Typography variant="h6" gutterBottom>Activation Failed</Typography>
            <Alert severity="error" sx={{ mb: 3, textAlign: 'left' }}>
              {message}
            </Alert>
            <Button
              variant="contained"
              color="secondary"
              onClick={() => navigate(loginPath)}
              fullWidth
            >
              Go to Sign In
            </Button>
          </>
        )}
      </Paper>
    </Box>
  );
};
